import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CountBadge,
  EmptyState,
  SEGMENTED_TRACK,
  segmentedItem,
  SkeletonList,
} from '../ui';
import { SuccessIcon, TargetIcon } from '../icons';
import { DAY_GRID } from './dayGrid';
import { DayTaskRow } from './DayTaskRow';
import type { TaskSummary } from '../../lib/tasks';
import { orderByDueDate } from '../../lib/taskGroups';
import type { DayTasks, DayWindow } from './useDayTasks';

/**
 * The day's work, across every project.
 *
 * "Today's Tasks" rather than "Due", because a due date is no longer the only
 * way onto this list: a task whose start date has arrived is work you are meant
 * to be doing, deadline or not, and the card that opens the morning is where it
 * belongs. `qualifies` in useDayTasks is the rule; this is the name for it.
 *
 * The rows only. Everything that fetches or changes them lives in useDayTasks
 * on the page, because the task panel opens over the dashboard and has to write
 * its edits back into this list -- see the comment there.
 */
const WINDOWS: { value: DayWindow; label: string; heading: string; nothing: string }[] = [
  { value: 'today', label: 'Today', heading: "Today's Tasks", nothing: 'Nothing on today' },
  {
    value: 'tomorrow',
    label: 'Tomorrow',
    // The heading follows the window rather than staying on "Today's Tasks":
    // the two sit a few pixels apart, and a card headed for today with
    // Tomorrow pressed beside it is a card that contradicts itself.
    heading: "Tomorrow's Tasks",
    nothing: 'Nothing on by tomorrow',
  },
];

export function DayTasksCard({
  className = '',
  day,
  activeTaskId,
  focusedIds,
  onToggleFocus,
}: {
  className?: string;
  day: DayTasks;
  /** The task open in the panel, so its row is marked underneath. */
  activeTaskId: string | null;
  /** Which of these tasks are already in the current session. A set rather than
   *  a predicate, because every row asks. */
  focusedIds: Set<string>;
  onToggleFocus: (task: TaskSummary) => void;
}) {
  const {
    dayWindow,
    setDayWindow,
    tasks,
    total,
    loading,
    loadError,
    actionError,
    setActionError,
    patch,
    flashId,
  } = day;

  /**
   * Whether picked work is folded away.
   *
   * Local to the card rather than held in useDayTasks, because nothing about it
   * reaches the server: the fetch is unchanged, and `focusedIds` is already
   * here for the row marks. The hook holds the window because the window is a
   * query; this is a way of reading the answer.
   *
   * Not persisted, unlike the projects view or the tasks grouping. Those are
   * how you prefer to read a page; this is a move you make partway through a
   * morning -- pick the four things, fold them away, work down what is left --
   * and it should not still be on tomorrow, hiding rows before you have picked
   * anything.
   */
  const [hidePicked, setHidePicked] = useState(false);

  // Non-null: WINDOWS covers every DayWindow, and the control can only set one
  // of them. `??` rather than a cast so a third window added to the type but
  // not to the list degrades to a card with a heading instead of a blank one.
  const current = WINDOWS.find((entry) => entry.value === dayWindow) ?? WINDOWS[0]!;

  // Sorted here rather than trusted from the fetch, as the Tasks pages do, so a
  // date changed from a row or from the panel moves the row in the same paint.
  // The server's order is already this one, so a fresh load is left as it came.
  const visible = orderByDueDate(
    hidePicked ? tasks.filter((task) => !focusedIds.has(task.id)) : tasks,
  );
  const hidden = tasks.length - visible.length;

  return (
    <Card className={className}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-4 py-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
          {current.heading}
          {/*
            What the card would hold, which is not always what the server
            counted: folding away four picked rows has to take four off the
            badge, or the number contradicts the list under it.

            `total - hidden` rather than `visible.length` because the list is
            capped and the badge is not -- it stays the whole answer, with the
            footer below saying how much of it did not fit. The arithmetic
            still lines up: badge = rows shown + rows not shown.
          */}
          {!loading && !loadError ? <CountBadge>{total - hidden}</CountBadge> : null}
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            The house toggle, worded and shaped as the Tasks page's completed
            one: a button whose label says what the next click does, with
            `aria-pressed` carrying the state a sighted reader gets from the
            word itself.

            Always here, even with nothing picked and nothing to fold. It is a
            way of reading the card rather than an action on it -- and picking
            happens *from these very rows*, so a button that appeared on the
            first pick would flicker in and out of a header while you worked.
          */}
          <Button
            compact
            aria-pressed={hidePicked}
            title="Hide the tasks already in your focus session"
            onClick={() => setHidePicked((value) => !value)}
          >
            {hidePicked ? 'Show picked' : 'Hide picked'}
          </Button>

          {/* A segmented control rather than a <select>: with two options both
              states are worth showing at once, and there is room for a third
              window later without it becoming a menu. */}
          <div role="group" aria-label="Which day" className={SEGMENTED_TRACK}>
            {WINDOWS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-pressed={dayWindow === value}
                onClick={() => setDayWindow(value)}
                className={segmentedItem(dayWindow === value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Above the list, not instead of it -- see the comment on `actionError`. */}
      {actionError ? (
        <div className="border-b border-stone-100 p-3">
          <Alert onDismiss={() => setActionError(null)}>{actionError}</Alert>
        </div>
      ) : null}

      {loadError ? (
        <div className="p-4">
          <Alert>{loadError}</Alert>
        </div>
      ) : loading ? (
        <SkeletonList rows={4} bordered={false} />
      ) : tasks.length === 0 ? (
        <div className="p-4">
          <EmptyState
            compact
            icon={<SuccessIcon className="h-5 w-5" />}
            title={current.nothing}
          >
            Overdue work, and anything already started, would show here too.
          </EmptyState>
        </div>
      ) : visible.length === 0 ? (
        /*
          Folded away to nothing. Its own state rather than the one above,
          which says the day is clear and would be a lie: there is work here,
          you have picked all of it, and the way back is a click. Marked with
          the session's own target rather than a tick for the same reason.
        */
        <div className="p-4">
          <EmptyState
            compact
            icon={<TargetIcon className="h-5 w-5" filled />}
            title="It is all in your session"
            action={
              <Button compact onClick={() => setHidePicked(false)}>
                Show picked
              </Button>
            }
          >
            {hidden === 1 ? 'One task is' : `All ${hidden} tasks are`} hidden — the Focus card
            above has what you are working on.
          </EmptyState>
        </div>
      ) : (
        <>
          {/* Same template as every row, so the labels sit over their columns. */}
          <div
            className={`${DAY_GRID} border-b border-stone-100 px-4 py-1.5 text-micro font-medium uppercase tracking-wide text-stone-500`}
          >
            {/* The label is wrapped rather than carrying `sr-only` itself:
                `sr-only` is `position: absolute`, which would take the element
                out of the grid flow and collapse the column it names. Same
                reason TaskList's header wraps its own. Below `sm:` the track is
                the width of the dot, so the word is hidden there and read out
                everywhere. */}
            <span>
              <span className="sr-only sm:not-sr-only">Status</span>
            </span>
            <span>Task</span>
            <span className="hidden sm:block">Project</span>
            <span>Due</span>
            <span className="hidden sm:block">
              <span className="sr-only">Priority</span>
            </span>
            {/* The row-action track. No label: the buttons under it are hover
                actions that name themselves, and a header word for them would
                be the widest thing in a 2.75rem column. */}
            <span aria-hidden="true" />
          </div>

          <ul className="divide-y divide-stone-100">
            {visible.map((task) => (
              <DayTaskRow
                key={task.id}
                task={task}
                active={task.id === activeTaskId}
                flashing={task.id === flashId}
                inFocus={focusedIds.has(task.id)}
                onPatch={(changes) => void patch(task, changes)}
                onToggleFocus={() => onToggleFocus(task)}
              />
            ))}
          </ul>

          {/*
            The two things the card is not showing, as one line.

            The cap is the server's and the fold is the reader's, but from here
            they are the same sentence: rows that exist and are not on screen.
            Saying only one of them would let a folded list read as the whole
            picture, which is the failure the cap line was added for.

            `total - tasks.length` for the cap, not `total - visible.length`:
            the folded rows came back from the server and are counted by the
            fold, so charging them to the cap as well would count them twice.
          */}
          {total > tasks.length || hidden > 0 ? (
            <p className="border-t border-stone-100 px-4 py-2 text-xs text-stone-500">
              {[
                total > tasks.length ? `+${total - tasks.length} more not shown` : null,
                hidden > 0 ? `${hidden} picked ${hidden === 1 ? 'task' : 'tasks'} hidden` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}
